import type { SalidaContexto } from '@/lib/types';
import { fmtInt } from '@/lib/format';
import { decimalSolicitud, formatoDecimal } from '@/lib/liquidacion';
import { Decimal } from '@/lib/offline/decimal';
import './SalidaForm.css';
import { useEffect, useRef } from 'react';

export interface SalidaFormValues {
  fecha: string;
  matadero: string;
  camion: string;
  albaran: string;
  observaciones: string;
  pesoSalida: string;
  fechaPesoSalida: string;
  pesoConocido: boolean;
  unidades: Record<number, string>;
}
export function SalidaForm({
  lotes,
  values,
  onChange,
  onUnidadesChange,
  errors,
  disabled,
  pesoOnly,
  fechaMin,
  fechaMax,
  total,
}: {
  lotes: SalidaContexto['lotes'];
  values: SalidaFormValues;
  onChange: (field: Exclude<keyof SalidaFormValues, 'unidades'>, value: string | boolean) => void;
  onUnidadesChange: (id: number, value: string) => void;
  errors: Record<string, string>;
  disabled: boolean;
  pesoOnly: boolean;
  fechaMin: string;
  fechaMax: string;
  total: number;
}) {
  const pesoDetails = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if ((errors.pesoSalida || errors.fechaPesoSalida) && pesoDetails.current)
      pesoDetails.current.open = true;
  }, [errors.pesoSalida, errors.fechaPesoSalida]);
  function field(
    name: Exclude<keyof SalidaFormValues, 'unidades' | 'pesoConocido'>,
    label: string,
    options: {
      type?: string;
      maxLength?: number;
      inputMode?: 'decimal';
      min?: string;
      max?: string;
    } = {},
  ) {
    return (
      <div className="cm-form-field" key={name}>
        <label htmlFor={`load-${name}`}>{label}</label>
        <input
          id={`load-${name}`}
          className="ui-field"
          value={values[name]}
          type={options.type ?? 'text'}
          maxLength={options.maxLength}
          inputMode={options.inputMode}
          min={options.min}
          max={options.max}
          autoComplete="off"
          disabled={disabled}
          onChange={(e) => onChange(name, e.target.value)}
          aria-invalid={!!errors[name]}
          aria-describedby={errors[name] ? `load-${name}-error` : undefined}
        />
        {errors[name] && (
          <p id={`load-${name}-error`} className="salida-field-error">
            {errors[name]}
          </p>
        )}
      </div>
    );
  }
  let average: string | null = null;
  try {
    if (
      total > 0 &&
      values.pesoSalida.trim() &&
      new Decimal(decimalSolicitud(values.pesoSalida)).compare(0) > 0
    )
      average = formatoDecimal(
        new Decimal(decimalSolicitud(values.pesoSalida)).div(total).toString(),
        2,
      );
  } catch {
    /* Keep invalid input available for correction. */
  }
  const pesoFields = (
    <div className="salida-weight-fields">
      {!pesoOnly && (
        <div className="salida-weight-choice">
          <label>
            <input
              type="radio"
              name="peso-conocido"
              disabled={disabled}
              checked={!values.pesoConocido}
              onChange={() => onChange('pesoConocido', false)}
            />
            Peso pendiente
          </label>
          <label>
            <input
              type="radio"
              name="peso-conocido"
              disabled={disabled}
              checked={values.pesoConocido}
              onChange={() => onChange('pesoConocido', true)}
            />
            Peso conocido
          </label>
        </div>
      )}
      {(pesoOnly || values.pesoConocido) && (
        <>
          {field('pesoSalida', 'Peso total en destino', { inputMode: 'decimal' })}
          {field('fechaPesoSalida', 'Fecha de recepción del peso', { type: 'date' })}
          <p className="cm-form-note">
            Fecha opcional. Al registrar el peso se completa automáticamente si falta.
          </p>
          {average && (
            <p className="salida-live-total">
              <span>Peso medio</span>
              <strong>{average} kg/ave</strong>
            </p>
          )}
        </>
      )}
      {!pesoOnly && !values.pesoConocido && (
        <p className="cm-form-note">
          Podrás completar el peso al recibirlo del matadero, también desde el cierre.
        </p>
      )}
    </div>
  );
  return (
    <div className="salida-form" data-testid="salida-formulario">
      {!pesoOnly && (
        <>
          <div className="salida-two-fields">
            {field('fecha', 'Fecha de carga', { type: 'date', min: fechaMin, max: fechaMax })}
            {field('matadero', 'Matadero', { maxLength: 200 })}
          </div>
          <section>
            <h2>Identificar la carga</h2>
            <p className="cm-form-note">Indica camión, albarán o ambos.</p>
            <div className="salida-two-fields">
              {field('camion', 'Camión / matrícula', { maxLength: 100 })}
              {field('albaran', 'Albarán', { maxLength: 100 })}
            </div>
          </section>
          <section>
            <h2>Animales por nave</h2>
            {lotes.map((lote) => (
              <div className="salida-animal-row" key={lote.lote_id}>
                <label htmlFor={`load-units-${lote.lote_id}`}>
                  <strong>{lote.nave_codigo}</strong>
                  <small>Saldo actual · {fmtInt(lote.aves_actuales)} aves</small>
                </label>
                <div>
                  <input
                    id={`load-units-${lote.lote_id}`}
                    className="ui-field"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    value={values.unidades[lote.lote_id] ?? ''}
                    placeholder="0"
                    disabled={disabled}
                    onChange={(e) => onUnidadesChange(lote.lote_id, e.target.value)}
                    aria-label={`Animales de ${lote.nave_codigo}`}
                    aria-invalid={!!errors[`unidades-${lote.lote_id}`]}
                    aria-describedby={
                      errors[`unidades-${lote.lote_id}`]
                        ? `load-units-${lote.lote_id}-error`
                        : undefined
                    }
                  />
                  <span>aves</span>
                </div>
                {errors[`unidades-${lote.lote_id}`] && (
                  <p className="salida-field-error" id={`load-units-${lote.lote_id}-error`}>
                    {errors[`unidades-${lote.lote_id}`]}
                  </p>
                )}
              </div>
            ))}
            <p className="cm-form-note">
              0 si la nave no participa. El saldo se valida al guardar.
            </p>
            {errors.unidades && <p className="salida-field-error">{errors.unidades}</p>}
            <div className="salida-live-total" aria-live="polite">
              <span>Total de la carga</span>
              <strong>{fmtInt(total)} aves</strong>
            </div>
          </section>
          <details className="salida-extra" ref={pesoDetails}>
            <summary>
              <span>Peso en destino</span>
              <small>
                {values.pesoConocido
                  ? `${formatoDecimal(decimalSolicitud(values.pesoSalida), 0)} kg`
                  : 'Pendiente'}
              </small>
            </summary>
            {pesoFields}
          </details>
          <details className="salida-extra">
            <summary>
              <span>Observaciones</span>
              <small>{values.observaciones ? 'Anotadas' : 'Opcional'}</small>
            </summary>
            <div className="cm-form-field">
              <label htmlFor="load-observaciones">Observaciones de la carga</label>
              <textarea
                id="load-observaciones"
                className="ui-field"
                value={values.observaciones}
                disabled={disabled}
                onChange={(e) => onChange('observaciones', e.target.value)}
              />
            </div>
          </details>
        </>
      )}
      {pesoOnly && pesoFields}
    </div>
  );
}
